import { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import cx from "classnames";
import InfiniteScroll from "src/components/infiniteScroll";
import TruncateWithTooltip from "src/components/truncateWithTooltip";
import { createPortal } from "react-dom";
import styles from "./dropdown.module.scss";

interface DropDownProps {
  selected: { key: string; label: string };
  options: Array<{ key: string; label: string }>;
  onChange: (key: string) => void;
  placeholder?: string;
  loadingLabel?: string;
  emptyLabel?: string;
  loading?: boolean;
  hasMore?: boolean;
  loadMore?: () => void;
}

export const DropDown: React.FC<DropDownProps> = ({
  selected, options, onChange, placeholder,
  loadingLabel = "Loading...", emptyLabel, loading, hasMore, loadMore,
}) => {
  const supportsInfiniteScroll = typeof loadMore === "function";
  const toggleRef = useRef<HTMLButtonElement>(null);
  const lastPosRef = useRef({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const focusedItemRef = useRef<HTMLElement | null>(null);
  const focusedIndexRef = useRef(-1);

  const [show, setShow] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const selectedOption = useMemo(
    () => options.find((o) => o.key === selected.key),
    [selected, options]
  );

  const setFocused = (index: number) => {
    focusedIndexRef.current = index;
    setFocusedIndex(index);
  };

  // scroll focused item into view
  useEffect(() => {
    focusedItemRef.current?.scrollIntoView({ block: "nearest" });
  }, [focusedIndex]);

  // reset on close
  useEffect(() => {
    if (!show) setFocused(-1);
  }, [show]);

  // click outside
  useEffect(() => {
    if (!show) return;
    const handleClickOutside = (e: MouseEvent) => {
      const insideToggle = toggleRef.current?.contains(e.target as Node);
      const insideMenu = menuRef.current?.contains(e.target as Node);
      if (!insideToggle && !insideMenu) setShow(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [show]);

  // close on scroll
  useEffect(() => {
    if (!show) return;
    const handleScroll = () => setShow(false);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [show]);

  useEffect(() => {
    if (!show) return;
  
    let rafId: number;

    const syncPosition = () => {
      if (toggleRef.current) {
        const rect = toggleRef.current.getBoundingClientRect();
        const newTop = rect.bottom + 6;
        const newLeft = rect.left;
        if (newTop !== lastPosRef.current.top || newLeft !== lastPosRef.current.left) {
          lastPosRef.current = { top: newTop, left: newLeft };
          setMenuPos({ top: newTop, left: newLeft });
        }
      }
      rafId = requestAnimationFrame(syncPosition);
    };
  
    rafId = requestAnimationFrame(syncPosition);
  
    return () => cancelAnimationFrame(rafId);
  }, [show]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!show) return;
  
    switch (e.key) {
      case "ArrowDown":
      case "ArrowUp":
      case "Enter":
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        break;
      default:
        return;
    }
  
    switch (e.key) {
      case "ArrowDown":
        setFocused(Math.min(focusedIndexRef.current + 1, options.length - 1));
        break;
      case "ArrowUp":
        setFocused(Math.max(focusedIndexRef.current - 1, 0));
        break;
      case "Enter":
        if (focusedIndexRef.current >= 0 && options[focusedIndexRef.current]) {
          onChange(options[focusedIndexRef.current].key);
          setShow(false);
        }
        break;
      case "Escape":
        setShow(false);
        break;
    }
  };

  const handleToggle = () => {
    if (!show && toggleRef.current) {
      const rect = toggleRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 6, left: rect.left });
    }
    setShow((prev) => !prev);
  };

  const showMenu = loading || options.length >= 1;

  const renderItem = (option: { key: string; label: string }, index: number) => {
    const isSelected = option.key === selectedOption?.key;
    const isFocused = index === focusedIndex;
    return (
      <Dropdown.Item
        key={index}
        ref={isFocused ? (el: HTMLElement | null) => { focusedItemRef.current = el; } : null}
        className={cx(styles.portalItem, {
          [styles.active]: isSelected,
          [styles.focused]: isFocused,
        })}
        onClick={(e) => { e.stopPropagation(); setShow(false); onChange(option.key); }}
      >
        <TruncateWithTooltip
          className={styles.itemLabel}
          placement="top"
          text={option.label}
        />
      </Dropdown.Item>
    );
  };

  return (
    <Dropdown show={show} onToggle={handleToggle} className={styles.dropdown}>
      <Dropdown.Toggle ref={toggleRef} onKeyDown={handleKeyDown}>
        <TruncateWithTooltip
          className={styles.toggleLabel}
          placement="top"
          text={selectedOption?.label || selected.label || placeholder || ""}
        />
        <i className={cx("paperflite-dropdown", { [styles.openModal]: show })} />
      </Dropdown.Toggle>

      {show && showMenu && createPortal(
        <div
          ref={menuRef}
          style={{
            position: "fixed",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            top: menuPos.top,
            left: menuPos.left,
            zIndex: 9999,
            background: "#fff",
            borderRadius: "0.75rem",
            boxShadow: "0rem 0rem 1rem 0rem rgba(18,18,18,0.08)",
            padding: "16px",
            minWidth: "9rem",
          }}
        >
          {loading && options.length === 0 ? (
            <Dropdown.Item disabled className={styles.empty}>
              <span>{loadingLabel}</span>
            </Dropdown.Item>
          ) : options.length === 0 ? (
            <Dropdown.Item disabled className={styles.empty}>
              <span>{emptyLabel}</span>
            </Dropdown.Item>
          ) : supportsInfiniteScroll ? (
            <InfiniteScroll
              className={styles.scrollable}
              loading={loading}
              hasMore={hasMore}
              loadMore={loadMore!}
            >
              {options.map(renderItem)}
            </InfiniteScroll>
          ) : (
            options.map(renderItem)
          )}
        </div>,
        document.body,
      )}
    </Dropdown>
  );
};